import {
  Campaign, CampaignStepEnum, ChatMessage, CreateCampaign, UpdateCampaign, PatchCampaign, ReadPage, StreamEvent,
  GenerationResultDto, AudienceChangeDto, UpdateLeadForm, CancelGenerationResultDto,
  LogoRematchRequestDto, LogoRematchResultDto
} from '@tinct/core-api-models';
import {ApiClient} from '../api-client/api-client';
import {ApiCollectionResponse, GetCollectionQueryParams, GetQueryByIdParams,} from '../types';
import {ApiError} from "../api-client/api-error";

export interface GetCampaignsFilter {
  organizationId?: string;
}

export interface GetCampaignPagesStaticFilter {
  campaignId: string;
}

export interface GetChatAgentMessagesStaticFilter {
  campaignId: string;
}

export class CampaignsEndpoint {
  constructor(private client: ApiClient) {}

  async getCampaign(params: GetQueryByIdParams<Campaign, Record<string, string>>): Promise<Campaign> {
    return this.client.getById<Campaign>(
      `/api/v1/campaigns/${params.id}`,
      params.fields,
      params.staticParams ? { params: params.staticParams } : undefined,
    );
  }

  async getCampaigns(
    params: GetCollectionQueryParams<Campaign, GetCampaignsFilter>,
  ): Promise<ApiCollectionResponse<Campaign>> {
    const { fields, offset, limit, filters, staticParams, sort } = params;
    return this.client.getCollection<Campaign>('/api/v1/campaigns', fields, {
      offset,
      limit,
      ...filters,
      ...staticParams,
      sort,
    });
  }


  async getCampaignPages(
    params: GetCollectionQueryParams<ReadPage, GetCampaignPagesStaticFilter>,
  ): Promise<ApiCollectionResponse<ReadPage>> {
    const { staticParams, fields, sort, offset = 0, limit = 20, filters } = params;

    return this.client.getCollection<ReadPage>(
      `/api/v1/pages?campaign_id=${staticParams?.campaignId}`,
      fields,
      {
        offset,
        limit,
        ...filters,
        sort,
      },
    );
  }

  async getCampaignChatAgentMessages(
    params: GetCollectionQueryParams<ChatMessage, GetChatAgentMessagesStaticFilter>,
  ): Promise<ApiCollectionResponse<ChatMessage>> {
    const { staticParams, fields, offset = 0, limit = 20, filters } = params;

    return this.client.getCollection<ChatMessage>(
      `/api/v1/campaigns/${staticParams?.campaignId}/chat-agent/messages`,
      fields,
      {
        offset,
        limit,
        ...filters,
      },
    );
  }

  async createCampaign(campaign: CreateCampaign): Promise<Campaign> {
    return this.client.post<Campaign>('/api/v1/campaigns', campaign);
  }

  async updateCampaign(id: string, data: UpdateCampaign): Promise<Campaign> {
    return this.client.put<Campaign>(`/api/v1/campaigns/${id}`, data);
  }

  async patchCampaign(id: string, data: PatchCampaign): Promise<Campaign> {
    return this.client.patch<Campaign>(`/api/v1/campaigns/${id}`, data);
  }

  async deleteCampaign(id: string): Promise<void> {
    return this.client.delete(`/api/v1/campaigns/${id}`);
  }

  async publishCampaign(id: string): Promise<Campaign> {
    return this.client.post<Campaign>(`/api/v1/campaigns/${id}/_publish`, {});
  }

  /**
   * Create-or-regenerate the campaign's hosted page and start generation
   * (202, runs in background — poll `campaign.landingPage.status`). 409 when
   * origin ≠ NEW, context unconfirmed, no brand source, or a run is in flight.
   * `leadForm` is persisted before generation starts so the FIRST generated
   * version renders the form; omitting it leaves any stored config untouched.
   */
  async startLandingPageGeneration(
    id: string,
    leadForm?: UpdateLeadForm | null,
  ): Promise<void> {
    await this.client.post<void>(
      `/api/v1/campaigns/${id}/landing-page-generation`,
      leadForm ? { leadForm } : {},
    );
  }

  /**
   * Generate a page variant targeting a Google Ads campaign (202 — runs as a
   * generation job like lead variants; poll the pages list).
   */
  async generateAdPage(
    campaignId: string,
    data: { adsCampaignId: string; userPrompt?: string },
  ): Promise<void> {
    await this.client.post<void>(
      `/api/v1/campaigns/${campaignId}/ad-pages`,
      data,
    );
  }

  /** Latest ready version goes live with the snippet tag baked in (synchronous). */
  async publishLandingPage(id: string): Promise<Campaign> {
    return this.client.post<Campaign>(`/api/v1/campaigns/${id}/_publish-landing-page`, {});
  }

  /** Takes the hosted page down. */
  async unpublishLandingPage(id: string): Promise<Campaign> {
    return this.client.post<Campaign>(`/api/v1/campaigns/${id}/_unpublish-landing-page`, {});
  }

  async unpublishCampaign(id: string): Promise<Campaign> {
    return this.client.post<Campaign>(`/api/v1/campaigns/${id}/_unpublish`, {});
  }

  async exportCampaignPagesStaticUrls(id: string): Promise<Blob> {
    const response = await this.client.request<Blob>(
      `/api/v1/campaigns/${id}/_export-static-urls`,
      {
        method: 'GET',
        responseType: 'blob',
      },
    );
    return response.data;
  }

  async validateCampaignAudience(id: string, excludeConflicts?: boolean): Promise<GenerationResultDto> {
    return this.client.post<GenerationResultDto>(
      `/api/v1/campaigns/${id}/validate-audience`,
      {},
      excludeConflicts ? { params: { exclude_conflicts: true } } : undefined,
    );
  }

  // Non-mutating preview of the impact of switching this campaign to `audienceId`.
  // Used to warn the user before the audience change is actually applied.
  async previewAudienceChange(id: string, audienceId: string): Promise<AudienceChangeDto> {
    return this.client.post<AudienceChangeDto>(
      `/api/v1/campaigns/${id}/preview-audience-change`,
      { audience: { id: audienceId } },
    );
  }

  async generateAll(id: string): Promise<GenerationResultDto> {
    return this.client.post<GenerationResultDto>(`/api/v1/campaigns/${id}/_generate-all`, {});
  }

  async regenerateOnError(id: string): Promise<GenerationResultDto> {
    return this.client.post<GenerationResultDto>(`/api/v1/campaigns/${id}/_regenerate-on-error`, {});
  }

  async cancelGeneration(id: string): Promise<CancelGenerationResultDto> {
    return this.client.post<CancelGenerationResultDto>(`/api/v1/campaigns/${id}/_cancel-generation`, {});
  }

  // Only the pages flagged needs_regeneration (see Campaign.pagesToRegenerateCount).
  // 403 / error code 5105 when the workspace lacks the generation credits.
  async regenerateOutdated(id: string): Promise<GenerationResultDto> {
    return this.client.post<GenerationResultDto>(`/api/v1/campaigns/${id}/_regenerate-outdated`, {});
  }

  // Re-runs the customer-logo matchmaking on the campaign's generated pages after
  // the logo library changed (only the CUSTOMER_LOGO variants are rewritten: no
  // regeneration, no credit). jobId is null when nothing needed re-matching.
  async rematchLogos(id: string, body: LogoRematchRequestDto = {}): Promise<LogoRematchResultDto> {
    return this.client.post<LogoRematchResultDto>(`/api/v1/campaigns/${id}/_rematch-logos`, body);
  }

  async startCampaignChat(
    campaignId: string,
    onEvent?: (event: StreamEvent) => void,
    onError?: (error: ApiError) => void,
    onComplete?: () => void
  ): Promise<void> {
    return this.client.postStream(
      `/api/v1/campaigns/${campaignId}/chat-agent/start`,
      {},
      onEvent,
      onError,
      onComplete
    );
  }

  async postCampaignMessageChat(
    campaignId: string,
    message: string,
    onEvent?: (event: StreamEvent) => void,
    onError?: (error: ApiError) => void,
    onComplete?: () => void
  ): Promise<void> {
    return this.client.postStream(
      `/api/v1/campaigns/${campaignId}/chat-agent`,
      {
        message,
      },
      onEvent,
      onError,
      onComplete
    );
  }

  async addConfirmedStep(id: string, confirmedStep: CampaignStepEnum): Promise<Campaign> {
    return this.client.put<Campaign>(`/api/v1/campaigns/${id}/confirmed-step/${confirmedStep}`, {});
  }

  async initCampaignLandingPageAnalyserAgent(
    campaignId: string,
    onEvent?: (event: StreamEvent) => void,
    onError?: (error: ApiError) => void,
    onComplete?: () => void
  ): Promise<void> {
    return this.client.postStream(
      `/api/v1/campaigns/${campaignId}/landing-page-analysis`,
      {
        strategy: 'llm-simplified-html'
      },
      onEvent,
      onError,
      onComplete
    );
  }
}
